import React from "react";

const Toolbar = ({ selectedElement, onCopy, onDelete, onFlipHorizontal, onExport, onExportStrip, onAddText, onAddShape, onBringToFront, onSendToBack, canvasDimensions, onCanvasDimensionChange, onSaveScene, onLoadScene, onClearScene, onReloadDefaultScene, drawingMode, onToggleDrawingMode, panels, currentPanelIndex, viewMode, onSelectPanel, onDeletePanel, onNextPanel, onPreviousPanel, onToggleViewMode, onAddPageBefore, onAddPageAfter, duplicateOnAdd, onToggleDuplicateOnAdd, onUploadImage }) => {
  const hasSelection = selectedElement !== null;

  // Common panel dimensions
  const panelDimensions = [
    { label: "1:1 (Square)", width: 600, height: 600 },
    { label: "4:3 (Landscape)", width: 800, height: 600 },
    { label: "3:4 (Portrait)", width: 600, height: 800 },
    { label: "16:9 (Widescreen)", width: 800, height: 450 },
    { label: "9:16 (Mobile)", width: 450, height: 800 },
    { label: "2:1 (Ultrawide)", width: 800, height: 400 },
    { label: "1:2 (Tall)", width: 400, height: 800 }
  ];

  // No templates; pages are added before/after current

  const handleDimensionChange = (e) => {
    const selectedOption = panelDimensions[e.target.value];
    if (selectedOption) {
      onCanvasDimensionChange(selectedOption);
    }
  };

  // Find current dimension index
  const currentDimensionIndex = panelDimensions.findIndex(
    dim => dim.width === canvasDimensions.width && dim.height === canvasDimensions.height
  );

  return (
    <div className="toolbar">
      <div className="toolbar-group">
        <button
          className="toolbar-button"
          onClick={onCopy}
          disabled={!hasSelection}
          title="Copy selected element (Ctrl+C)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={onDelete}
          disabled={!hasSelection}
          title="Delete selected element (Delete)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
          </svg>
        </button>
      </div>

      <div className="toolbar-group">
        <button
          className="toolbar-button"
          onClick={onFlipHorizontal}
          disabled={!hasSelection}
          title="Flip horizontally"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="7 8 3 12 7 16"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <polyline points="17 8 21 12 17 16"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={onBringToFront}
          disabled={!hasSelection}
          title="Bring to front"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 11h3v10c0 .55.45 1 1 1s1-.45 1-1V11h3c.55 0 1-.45 1-1s-.45-1-1-1h-3V7c0-.55-.45-1-1-1s-1 .45-1 1v3H8c-.55 0-1 .45-1 1s.45 1 1 1z"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={onSendToBack}
          disabled={!hasSelection}
          title="Send to back"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 11h3v10c0 .55.45 1 1 1s1-.45 1-1V11h3c.55 0 1-.45 1-1s-.45-1-1-1h-3V7c0-.55-.45-1-1-1s-1 .45-1 1v3H8c-.55 0-1 .45-1 1s.45 1 1 1z"/>
          </svg>
        </button>
      </div>

      <div className="toolbar-group">
        <button
          className={`toolbar-button ${drawingMode ? 'active' : ''}`}
          onClick={onToggleDrawingMode}
          title={drawingMode ? "Exit drawing mode" : "Enter drawing mode"}
          disabled={viewMode === 'strip'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={() => onAddShape('rectangle')}
          title="Add rectangle"
          disabled={viewMode === 'strip'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={() => onAddShape('circle')}
          title="Add circle"
          disabled={viewMode === 'strip'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={() => onAddShape('line')}
          title="Add line"
          disabled={viewMode === 'strip'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={onAddText}
          title="Add text element"
          disabled={viewMode === 'strip'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.5 4v3h5v12h3V7h5V4H2.5zM21.5 9h-9v3h9V9z"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={onUploadImage}
          title="Upload image"
          disabled={viewMode === 'strip'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 7v2.99s-1.99.01-2 0V7h-3s.01-1.99 0-2h3V2h2v3h3v2h-3zm-3 4V8h-3V5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8h-3zM5 19l3-4 2 3 3-4 4 5H5z"/>
          </svg>
        </button>
      </div>

      <div className="toolbar-group">
        <button
          className="toolbar-button"
          onClick={onAddPageBefore}
          title="Add page before"
          disabled={viewMode === 'strip'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13H5v-2h14v2zM9 19H7v-4H3v-2h4V9h2v4h4v2H9v4z"/>
          </svg>
        </button>
        <button
          className="toolbar-button"
          onClick={onAddPageAfter}
          title="Add page after"
          disabled={viewMode === 'strip'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13H5v-2h14v2zM17 19h-2v-4h-4v-2h4V9h2v4h4v2h-4v4z"/>
          </svg>
        </button>
        <button
          className={`toolbar-button ${duplicateOnAdd ? 'active' : ''}`}
          onClick={onToggleDuplicateOnAdd}
          title={duplicateOnAdd ? 'Duplicate on add: ON' : 'Duplicate on add: OFF'}
          disabled={viewMode === 'strip'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
          </svg>
        </button>
        
        {panels.length > 0 && (
          <>
            <div className="toolbar-dropdown">
              <select
                value={currentPanelIndex}
                onChange={(e) => onSelectPanel(parseInt(e.target.value))}
                title="Select panel"
                disabled={viewMode === 'strip'}
              >
                {panels.map((panel, index) => (
                  <option key={panel.id} value={index}>
                    {panel.label}
                  </option>
                ))}
              </select>
            </div>
            
            <button
              className="toolbar-button"
              onClick={onPreviousPanel}
              disabled={viewMode === 'strip' || currentPanelIndex === 0}
              title="Previous panel"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>
            
            <button
              className="toolbar-button"
              onClick={onNextPanel}
              disabled={viewMode === 'strip' || currentPanelIndex === panels.length - 1}
              title="Next panel"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </button>
            
            
            
            <button
              className="toolbar-button"
              onClick={() => onDeletePanel(currentPanelIndex)}
              disabled={panels.length <= 1}
              title="Delete current panel"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </>
        )}
      </div>

      <div className="toolbar-group">
        <select
          className="toolbar-dropdown"
          value={currentDimensionIndex}
          onChange={handleDimensionChange}
          title="Change panel dimensions"
        >
          {panelDimensions.map((dim, index) => (
            <option key={index} value={index}>
              {dim.label}
            </option>
          ))}
        </select>
      </div>

      <div className="toolbar-group">
        <button
          className="toolbar-button"
          onClick={onSaveScene}
          title="Save scene as JSON file"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={onLoadScene}
          title="Load scene from JSON file"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={onClearScene}
          title="Clear current scene"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
          </svg>
        </button>
        
        <button
          className="toolbar-button"
          onClick={onReloadDefaultScene}
          title="Reload default scene"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"/>
          </svg>
        </button>
      </div>

      <div className="toolbar-group">
        <button
          className="toolbar-button"
          onClick={onExport}
          title="Export current page as PNG"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
          </svg>
        </button>
        <button
          className="toolbar-button"
          onClick={onExportStrip}
          title="Export full strip as PNG"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 5h18v2H3zM3 11h18v2H3zM3 17h18v2H3z"/>
          </svg>
        </button>
        <button
          className={`toolbar-button ${viewMode === 'strip' ? 'active' : ''}`}
          onClick={onToggleViewMode}
          title={viewMode === 'panel' ? 'Print preview' : 'Exit preview'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        </button>
        {viewMode === 'strip' && (
          <div className="toolbar-dropdown" title="Rows in strip preview">
            <select
              defaultValue={1}
              onChange={(e) => {
                const event = new CustomEvent('stripRowsChange', { detail: parseInt(e.target.value, 10) || 1 });
                window.dispatchEvent(event);
              }}
            >
              {[1,2,3,4].map(r => (
                <option key={r} value={r}>{r} {r===1? 'row' : 'rows'}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default Toolbar;
